(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    define([], function(){ return factory(root); });
  } else if (typeof module === 'object' && module.exports) {
    module.exports = factory(globalThis || (typeof self !== 'undefined' ? self : this));
  } else {
    root.SwishRacquetballLines = factory(root);
  }
}(typeof self !== 'undefined' ? self : this, function (GLOBAL) {
  'use strict';

  const SKIP_COLOR = '__SKIP__';
  const INCH_M = 0.0254;

  function normHex(value) {
    let hex = String(value || '#FFFFFF').trim();
    if (hex.charAt(0) !== '#') hex = '#' + hex;
    return hex.toUpperCase();
  }

  function hasKeys(value) {
    if (!value) return false;
    for (const key in value) return true;
    return false;
  }

  function resolveOfficial() {
    const config = GLOBAL && GLOBAL.SwishCBConfig;
    if (config && typeof config.resolveOfficialCourtGeometry === 'function') {
      try { return config.resolveOfficialCourtGeometry('RACQUETBALL', { noSeries: true }); } catch (_) {}
    }
    return null;
  }

  const RESOLVED = resolveOfficial();
  const RAW = (RESOLVED && RESOLVED.official) || {};
  const FEATURE = (RESOLVED && RESOLVED.features && RESOLVED.features.racquetball) || {};
  const COURT = Object.freeze({
    LENGTH: Number(RESOLVED && RESOLVED.officialLengthM) || 12.192,
    WIDTH: Number(RESOLVED && RESOLVED.officialWidthM) || 6.096,
    LINE: Number(RESOLVED && RESOLVED.lineWidthM) || (1.5 * INCH_M),
    SERVICE_FRONT_EDGE: Number(FEATURE.serviceLineFrontEdgeFromFrontWallM) || Number(RAW.serviceLineFrontEdgeFromFrontWall) || 4.572,
    SERVICE_CENTER: Number(FEATURE.serviceLineStrokeCenterFromFrontWallM) || 4.59105,
    SHORT_BACK_EDGE: Number(FEATURE.shortLineBackEdgeFromFrontWallM) || Number(RAW.shortLineBackEdgeFromFrontWall) || 6.096,
    SHORT_CENTER: Number(FEATURE.shortLineStrokeCenterFromFrontWallM) || 6.07695,
    RECEIVING_BACK_EDGE: Number(FEATURE.receivingLineBackEdgeFromFrontWallM) || Number(RAW.receivingLineBackEdgeFromFrontWall) || 7.62,
    RECEIVING_CENTER: Number(FEATURE.receivingLineStrokeCenterFromFrontWallM) || 7.60095,
    SERVICE_BOX_CENTER: Number(FEATURE.serviceBoxLineStrokeCenterFromSideWallM) || 0.43815,
    DRIVE_SERVE_CENTER: Number(FEATURE.driveServeLineStrokeCenterFromSideWallM) || 0.89535,
    WALL_SEGMENT: Number(RAW.receivingWallSegment) || 0.5334,
    DASH_LENGTH: Number(RAW.receivingDashLength) || 0.1524,
    DASH_GAP: Number(RAW.receivingGapLength) || 0.1524,
    DASH_COUNT: Math.max(0, Math.round(Number(RAW.receivingDashCount) || 16))
  });

  function makePart(id, order, role, fallback, draw) {
    return { id, order, role, fallback, side: 'center', draw };
  }

  function snapTileCoordinate(dims, value, axis, mode) {
    const tileM = Math.max(0, Number(dims && dims.tileSizeM) || 0);
    if (!(tileM > 0)) return value;
    const offset = axis === 'y'
      ? (Number(dims && dims.tileGridOffsetYM) || 0)
      : (Number(dims && dims.tileGridOffsetXM) || 0);
    const normalized = (offset + value) / tileM;
    const epsilon = 1e-9;
    let tileIndex;
    if (mode === 'start') tileIndex = Math.floor(normalized + epsilon);
    else if (mode === 'end') tileIndex = Math.ceil(normalized - epsilon);
    else tileIndex = Math.floor(normalized + 0.5 - epsilon);
    return (tileIndex * tileM) - offset;
  }

  function resolveAreaGeometry(dims, court) {
    const painted = {
      x0: court.x,
      service: court.serviceFrontEdge,
      short: court.shortBackEdge,
      receiving: court.receivingBackEdge,
      x1: court.right,
      y0: court.y,
      y1: court.bottom
    };
    const tileM = Math.max(0, Number(dims && dims.tileSizeM) || 0);
    if (!(tileM > 0) || String(dims && dims.lineFitMode || '') !== 'clean') return painted;

    const clampX = (value) => Math.max(0, Math.min(dims.surfaceLength, value));
    const clampY = (value) => Math.max(0, Math.min(dims.surfaceWidth, value));
    const x0 = clampX(snapTileCoordinate(dims, court.x, 'x', 'start'));
    const x1 = clampX(snapTileCoordinate(dims, court.right, 'x', 'end'));
    const y0 = clampY(snapTileCoordinate(dims, court.y, 'y', 'start'));
    const y1 = clampY(snapTileCoordinate(dims, court.bottom, 'y', 'end'));
    const orderedDivider = (value, minimum, maximum) => Math.max(
      minimum,
      Math.min(maximum, clampX(snapTileCoordinate(dims, value, 'x', 'nearest')))
    );
    const service = orderedDivider(court.serviceFrontEdge, x0, x1);
    const short = orderedDivider(court.shortBackEdge, service, x1);
    const receiving = orderedDivider(court.receivingBackEdge, short, x1);
    return { x0, service, short, receiving, x1, y0, y1 };
  }

  function fillAreaRect(ctx, dims, color, x, y, w, h) {
    if (!(w > 0) || !(h > 0)) return;
    ctx.fillStyle = (GLOBAL.SwishCBHardwood && typeof GLOBAL.SwishCBHardwood.getFillStyle === 'function')
      ? GLOBAL.SwishCBHardwood.getFillStyle(ctx, color, { hardwood: dims.hardwood, pattern: dims.pattern, patternTarget: dims.patternTarget, pixelsPerMeter: dims.scaleAvg })
      : color;
    ctx.fillRect(x * dims.scaleX, y * dims.scaleY, w * dims.scaleX, h * dims.scaleY);
  }

  function resolveCourtGeometry(dims) {
    const surfaceLength = Math.max(0.001, Number(dims && dims.surfaceLength) || COURT.LENGTH);
    const surfaceWidth = Math.max(0.001, Number(dims && dims.surfaceWidth) || COURT.WIDTH);
    const lineScale = Math.min(1, surfaceLength / COURT.LENGTH, surfaceWidth / COURT.WIDTH);
    const length = COURT.LENGTH * lineScale;
    const width = COURT.WIDTH * lineScale;
    const x = Math.max(0, (surfaceLength - length) * 0.5);
    const y = Math.max(0, (surfaceWidth - width) * 0.5);
    const sx = (value) => x + (value * lineScale);
    const sy = (value) => y + (value * lineScale);
    const receivingSegments = [];
    receivingSegments.push({ y1: y, y2: sy(COURT.WALL_SEGMENT), kind: 'wall-segment' });
    let segmentStart = COURT.WALL_SEGMENT + COURT.DASH_GAP;
    for (let index = 0; index < COURT.DASH_COUNT; index += 1) {
      receivingSegments.push({
        y1: sy(segmentStart),
        y2: sy(segmentStart + COURT.DASH_LENGTH),
        kind: 'dash',
        index
      });
      segmentStart += COURT.DASH_LENGTH + COURT.DASH_GAP;
    }
    receivingSegments.push({ y1: sy(COURT.WIDTH - COURT.WALL_SEGMENT), y2: y + width, kind: 'wall-segment' });
    return {
      x,
      y,
      right: x + length,
      bottom: y + width,
      length,
      width,
      lineScale,
      lineWidth: COURT.LINE * lineScale,
      serviceFrontEdge: sx(COURT.SERVICE_FRONT_EDGE),
      serviceCenter: sx(COURT.SERVICE_CENTER),
      shortBackEdge: sx(COURT.SHORT_BACK_EDGE),
      shortCenter: sx(COURT.SHORT_CENTER),
      receivingBackEdge: sx(COURT.RECEIVING_BACK_EDGE),
      receivingCenter: sx(COURT.RECEIVING_CENTER),
      serviceBoxTop: sy(COURT.SERVICE_BOX_CENTER),
      serviceBoxBottom: sy(COURT.WIDTH - COURT.SERVICE_BOX_CENTER),
      driveServeTop: sy(COURT.DRIVE_SERVE_CENTER),
      driveServeBottom: sy(COURT.WIDTH - COURT.DRIVE_SERVE_CENTER),
      receivingSegments
    };
  }

  function fillLineV(ctx, dims, color, xM, y1M, y2M, widthM) {
    const widthPx = dims.fixedLineWidthPx || (widthM * dims.scaleAvg);
    const xPx = xM * dims.scaleX;
    const y1Px = y1M * dims.scaleY;
    const y2Px = y2M * dims.scaleY;
    ctx.fillStyle = color;
    ctx.fillRect(xPx - (widthPx * 0.5), Math.min(y1Px, y2Px), widthPx, Math.abs(y2Px - y1Px));
  }

  function fillLineH(ctx, dims, color, x1M, x2M, yM, widthM) {
    const widthPx = dims.fixedLineWidthPx || (widthM * dims.scaleAvg);
    const x1Px = x1M * dims.scaleX;
    const x2Px = x2M * dims.scaleX;
    const yPx = yM * dims.scaleY;
    ctx.fillStyle = color;
    ctx.fillRect(Math.min(x1Px, x2Px), yPx - (widthPx * 0.5), Math.abs(x2Px - x1Px), widthPx);
  }

  const PARTS = [
    makePart('racquetMainCourtArea', 10, 'area', 'main', (ctx, dims, color) => {
      const area = dims.area;
      fillAreaRect(ctx, dims, color, area.x0, area.y0, area.service - area.x0, area.y1 - area.y0);
      fillAreaRect(ctx, dims, color, area.receiving, area.y0, area.x1 - area.receiving, area.y1 - area.y0);
    }),
    makePart('racquetServiceZoneArea', 20, 'area', 'key', (ctx, dims, color) => {
      const area = dims.area;
      fillAreaRect(ctx, dims, color, area.service, area.y0, area.short - area.service, area.y1 - area.y0);
    }),
    makePart('racquetSafetyZoneArea', 30, 'area', 'border', (ctx, dims, color) => {
      const area = dims.area;
      fillAreaRect(ctx, dims, color, area.short, area.y0, area.receiving - area.short, area.y1 - area.y0);
    }),
    // Racquetball's physical walls are the official boundary. The builder still
    // needs a visible footprint boundary when optional surrounding flooring is
    // present, so this visual-only line is deliberately opt-in and is not part
    // of Additional Sports Racquetball linework.
    makePart('racquetPlayingBoundaryLines', 190, 'line', 'lines', (ctx, dims, color) => {
      if (!dims.showBoundary) return;
      const court = dims.court;
      const half = court.lineWidth * 0.5;
      fillLineH(ctx, dims, color, court.x, court.right, court.y + half, court.lineWidth);
      fillLineH(ctx, dims, color, court.x, court.right, court.bottom - half, court.lineWidth);
      fillLineV(ctx, dims, color, court.x + half, court.y, court.bottom, court.lineWidth);
      fillLineV(ctx, dims, color, court.right - half, court.y, court.bottom, court.lineWidth);
    }),
    makePart('racquetServiceLine', 200, 'line', 'lines', (ctx, dims, color) => {
      const court = dims.court;
      fillLineV(ctx, dims, color, court.serviceCenter, court.y, court.bottom, court.lineWidth);
    }),
    makePart('racquetShortLine', 210, 'line', 'lines', (ctx, dims, color) => {
      const court = dims.court;
      fillLineV(ctx, dims, color, court.shortCenter, court.y, court.bottom, court.lineWidth);
    }),
    makePart('racquetReceivingLine', 220, 'line', 'lines', (ctx, dims, color) => {
      const court = dims.court;
      court.receivingSegments.forEach((segment) => {
        fillLineV(ctx, dims, color, court.receivingCenter, segment.y1, segment.y2, court.lineWidth);
      });
    }),
    makePart('racquetServiceBoxLines', 230, 'line', 'lines', (ctx, dims, color) => {
      const court = dims.court;
      fillLineH(ctx, dims, color, court.serviceCenter, court.shortCenter, court.serviceBoxTop, court.lineWidth);
      fillLineH(ctx, dims, color, court.serviceCenter, court.shortCenter, court.serviceBoxBottom, court.lineWidth);
    }),
    makePart('racquetDriveServeLines', 240, 'line', 'lines', (ctx, dims, color) => {
      const court = dims.court;
      fillLineH(ctx, dims, color, court.serviceCenter, court.shortCenter, court.driveServeTop, court.lineWidth);
      fillLineH(ctx, dims, color, court.serviceCenter, court.shortCenter, court.driveServeBottom, court.lineWidth);
    })
  ];

  const PART_LOOKUP = PARTS.reduce((result, part) => {
    result[part.id] = part;
    return result;
  }, Object.create(null));
  const DRAW_ORDER = PARTS.slice().sort((a, b) => a.order - b.order).map(part => part.id);
  const LINE_ONLY_ORDER = DRAW_ORDER.filter(id => PART_LOOKUP[id] && PART_LOOKUP[id].role === 'line');

  function pickDetailColor(detailColors, fallbackColors, id, fallback, palette) {
    if (detailColors && Object.prototype.hasOwnProperty.call(detailColors, id)) {
      if (detailColors[id] === SKIP_COLOR) return null;
      return detailColors[id];
    }
    if (fallbackColors && Object.prototype.hasOwnProperty.call(fallbackColors, id)) {
      if (fallbackColors[id] === SKIP_COLOR) return null;
      return fallbackColors[id];
    }
    if (fallback === 'key') return palette.key;
    if (fallback === 'border') return palette.border;
    if (fallback === 'lines') return palette.lines;
    return palette.main;
  }

  function computeDims(cw, ch, opts) {
    const surfaceLength = Math.max(0.001, Number(opts && opts.playableLengthM) || COURT.LENGTH);
    const surfaceWidth = Math.max(0.001, Number(opts && opts.playableWidthM) || COURT.WIDTH);
    return {
      scaleX: cw / surfaceLength,
      scaleY: ch / surfaceWidth,
      scaleAvg: ((cw / surfaceLength) + (ch / surfaceWidth)) * 0.5,
      fixedLineWidthPx: Math.max(0, Number(opts && opts.fixedLineWidthPx) || 0),
      surfaceLength,
      surfaceWidth,
      lineFitMode: String(opts && opts.lineFitMode || '') === 'clean' ? 'clean' : 'painted',
      tileSizeM: Math.max(0, Number(opts && opts.tileSizeM) || 0),
      tileGridOffsetXM: Number(opts && opts.tileGridOffsetXM) || 0,
      tileGridOffsetYM: Number(opts && opts.tileGridOffsetYM) || 0,
      showBoundary: !!(opts && opts.showBoundary),
      hardwood: opts && opts.hardwood
    };
  }

  const DEFAULT_PALETTE = Object.freeze({
    main: '#5F5D5E',
    key: '#16A3CA',
    border: '#1C1A19',
    lines: '#FF0000'
  });

  function draw(ctx, x0, y0, cw, ch, color, opts) {
    if (!ctx || !cw || !ch) return;
    const detailColors = (opts && opts.detailColors) || null;
    const fallbackColors = (opts && opts.fallbackColors) || null;
    const drawDetailed = !!(opts && opts.useDetailedParts) || hasKeys(detailColors) || hasKeys(fallbackColors);
    const order = drawDetailed ? DRAW_ORDER : LINE_ONLY_ORDER;
    const dims = computeDims(cw, ch, opts);
    dims.hardwood = opts && opts.hardwood;
    dims.pattern = opts && opts.pattern;
    dims.patternTargetPrefix = opts && opts.patternTargetPrefix;
    dims.court = resolveCourtGeometry(dims);
    dims.area = resolveAreaGeometry(dims, dims.court);
    const palette = {
      main: DEFAULT_PALETTE.main,
      key: DEFAULT_PALETTE.key,
      border: DEFAULT_PALETTE.border,
      lines: normHex(color || DEFAULT_PALETTE.lines)
    };

    ctx.save();
    ctx.translate(x0, y0);
    order.forEach((id) => {
      const part = PART_LOOKUP[id];
      if (!part) return;
      const fill = pickDetailColor(detailColors, fallbackColors, id, part.fallback, palette);
      if (!fill) return;
      try {
        dims.patternTarget = { id: (dims.patternTargetPrefix || '') + id, role: part.role, fallback: part.fallback };
        part.draw(ctx, dims, normHex(fill));
      }
      catch (error) { console.warn('[SwishRacquetballLines] draw error for part', id, error); }
    });
    ctx.restore();
  }

  return {
    design: { W: COURT.WIDTH * 1000, L: COURT.LENGTH * 1000 },
    official: COURT,
    parts: PARTS.slice(),
    drawOrder: DRAW_ORDER.slice(),
    lineOnlyOrder: LINE_ONLY_ORDER.slice(),
    resolveCourtGeometry,
    resolveAreaGeometry,
    draw,
    ready: Promise.resolve()
  };
}));
